import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react'
import { fetchWithAuth } from '../utils/api'
import { useAuth } from '../contexts/AuthContext'
import TweetCard from './TweetCard'
import logger from '../utils/logger'

export default function Feed({ onOpenLightbox, onLoadingChange, onRefreshReady, onPullingChange }) {
  const [items, setItems] = useState([])
  const [selectedAuthor, setSelectedAuthor] = useState('all')
  const [selectedQuerySource, setSelectedQuerySource] = useState('all')
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(1)
  const [isBootstrapping, setIsBootstrapping] = useState(true)
  const [isFetchingAllPages, setIsFetchingAllPages] = useState(true)
  const [isPulling, setIsPulling] = useState(false)
  const [lastPulledAt, setLastPulledAt] = useState(null)
  const [nowTick, setNowTick] = useState(Date.now())
  const statusRef = useRef(null)
  const reconnectRef = useRef(1000)
  const lastHiddenTime = useRef(null)
  const { API_BASE: apiBaseRaw } = useAuth()
  const [readIds, setReadIds] = useState(() => {
    if (typeof window === 'undefined') return new Set()
    try {
      const saved = JSON.parse(localStorage.getItem('readTweetIds') || '[]')
      return new Set(Array.isArray(saved) ? saved : [])
    } catch {
      return new Set()
    }
  })

  // Debug: Log when items change
  useEffect(() => {
    logger.info('State', `Items array updated: ${items.length} total items`)
  }, [items.length])

  const PAGE_SIZE = 200

  const sanitizeText = (val) => (typeof val === 'string' ? val.replace(/\u0964/g, '') : val)
  const normalizeScore = (val) => {
    const n = Number(val)
    if (!Number.isFinite(n)) return null
    return Math.max(0, Math.min(10, Math.round(n)))
  }

  const sanitizeItem = (it = {}) => ({
    ...it,
    summary: sanitizeText(it.summary),
    one_line_summary: sanitizeText(it.one_line_summary),
  importance_score: normalizeScore(it.importance_score),
  })

  const dedupeAndSort = (list) => {
    const seen = new Set()
    const dedup = []
    for (const it of list) {
      const key = it._id || (it.tweet_id || it.tweet_url || '') + '::' + (it._ts || 0)
      if (seen.has(key)) continue
      seen.add(key)
      dedup.push(it)
    }
    dedup.sort((a, b) => (b._ts || 0) - (a._ts || 0))
    return dedup
  }

  // Use API_BASE from auth context
  const apiBaseClean = useMemo(() => (apiBaseRaw || '').replace(/\/+$/g, ''), [apiBaseRaw])

  const WS_URL = useMemo(() => {
    if (typeof window === 'undefined') return ''
    return (
      (import.meta.env && import.meta.env.VITE_WS_URL) ||
      (window && window.__WS_URL) ||
      `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/ws`
    )
  }, [])

  // heartbeat to refresh relative timestamps
  useEffect(() => {
    const t = setInterval(() => setNowTick(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])

  const bootstrapItems = useCallback(async () => {
    setIsFetchingAllPages(true)
    let firstPageRendered = false
    try {
      const PAGE_LIMIT = 200; // match backend MAX_LIMIT; if backend changes, this is a safe high value
      const maxPages = 50; // safety cap to avoid infinite loops (50 * 200 = 10k items)
      let all = []
      let before = undefined

      for (let pageNum = 0; pageNum < maxPages; pageNum++) {
        const params = new URLSearchParams()
        params.set('limit', String(PAGE_LIMIT))
        if (before) params.set('before', before)
        const url = `${apiBaseClean}/api/items?${params.toString()}`
        const resp = await fetchWithAuth(url, { headers: { 'Accept': 'application/json' } })
        const data = await resp.json()
        // support both array or {items: []}
        const arr = Array.isArray(data) ? data : (Array.isArray(data?.items) ? data.items : [])
        if (!Array.isArray(arr) || arr.length === 0) break

        // Append mapped items (preserve DB-provided timestamp fields)
        const mapped = arr.map((d) => {
          const ts = d.tweet_created_at || d.fetched_at || new Date().toISOString()
          const tsMs = Date.parse(ts) || Date.now()
          const _id = deriveId(d, tsMs)

          // Handle backwards compatibility for legacy tweets
          const source_type = d.source_type || 'from-handle'
          const query_source = d.query_source || d.account_handle || 'unknown'

            return {
            type: 'tweet_summary',
            timestamp: ts,
            _ts: tsMs,
            _id,
            account: d.account_handle,
            handle: d.account_handle,
            source_type: source_type,           // NEW
            query_source: query_source,         // NEW
            tweet: d.text_clean,
            summary: sanitizeText(d.summary),
            one_line_summary: sanitizeText(d.one_line_summary || d.one_line_headline),
            location: d.location,
              importance_score: normalizeScore(d.importance_score),
            author_name: d.author_name,
            author_username: d.author_username,
            author_profile_image_url: d.author_profile_image_url,
            author_twitter_url: d.author_twitter_url,
            tweet_url: d.tweet_url,
            media_urls: Array.isArray(d.media_urls) ? d.media_urls : [],
            tweet_id: d.tweet_id,
          }
        })

        all = all.concat(mapped)
        const deduped = dedupeAndSort(all)
        setItems(deduped)
        setPage(1)

        if (!firstPageRendered) {
          firstPageRendered = true
          setIsBootstrapping(false)
        }

        // If fewer than page limit returned, we're done.
        if (arr.length < PAGE_LIMIT) break

        // Otherwise page: set before to the ISO timestamp of the last returned item
        const last = arr[arr.length - 1]
        const lastTs = last?.tweet_created_at || last?.fetched_at
        if (!lastTs) break
        before = lastTs
      }

      if (!firstPageRendered) {
        // no data returned, end loading
        setIsBootstrapping(false)
      }
    } catch (e) {
      logger.exception('State', 'Failed to bootstrap items', e)
      setIsBootstrapping(false)
    } finally {
      setIsFetchingAllPages(false)
    }
  }, [apiBaseClean])

  const persistReadIds = (nextSet) => {
    try {
      localStorage.setItem('readTweetIds', JSON.stringify(Array.from(nextSet)))
    } catch (e) {
      logger.exception('State', 'Failed to persist read tweets', e)
    }
  }

  const markRead = (id) => {
    if (!id) return
    setReadIds((prev) => {
      if (prev.has(id)) return prev
      const next = new Set(prev)
      next.add(id)
      persistReadIds(next)
      return next
    })
  }

  const deriveId = (it, tsMs) => {
    return it?.tweet_id || it?.tweet_url || it?.id || String(tsMs || Date.now())
  }

  useEffect(() => {
    const statusEl = document.getElementById("status")
    statusRef.current = statusEl

    let ws;
    let shouldStop = false;
    let reconnectTimer = null;

    function connect() {
      if (!WS_URL) return
      ws = new WebSocket(WS_URL)

      ws.addEventListener("open", () => {
        // Send authentication message
        const token = localStorage.getItem('auth_token')
        if (token) {
          ws.send(JSON.stringify({
            type: 'auth',
            token: token
          }))
        }

        if (statusEl) {
          statusEl.textContent = "Authenticatingâ€¦";
          statusEl.style.color = "#ffd700";
        }

        // Reset reconnect delay after a successful connection.
        reconnectRef.current = 1000;
      });

      ws.addEventListener("close", () => {
        if (statusEl) {
          statusEl.textContent = "Disconnected";
          statusEl.style.color = "#ff6b6b";
        }
        if (!shouldStop) {
          // exponential-ish reconnect with cap
          const delay = Math.min(30000, (reconnectRef.current || 1000) * 2);
          reconnectRef.current = delay;
          if (reconnectTimer) clearTimeout(reconnectTimer);
          reconnectTimer = setTimeout(connect, delay);
        }
      });

      ws.addEventListener("error", (err) => {
        logger.exception('ws', 'WS error', err)
      });

      ws.addEventListener("message", (evt) => {
        try {
          const parsed = JSON.parse(evt.data);
          logger.info('WebSocket', 'Received message', parsed);

          // Handle auth responses
          if (parsed.type === "auth_success") {
            logger.info('WebSocket', 'Authentication successful');
            if (statusEl) {
              statusEl.textContent = "Connected";
              statusEl.style.color = "#22c55e";
            }
            return;
          }

          if (parsed.type === "auth_failed") {
            logger.error('ws', `Authentication failed: ${parsed.error}`);
            if (statusEl) {
              statusEl.textContent = "Auth Failed";
              statusEl.style.color = "#ff6b6b";
            }
            ws.close();
            return;
          }

          // Handle tweet summaries
          const msg = sanitizeItem(parsed)
          if (msg.type === "tweet_summary") {
            logger.info('WebSocket', 'Processing tweet_summary', msg);
            const tsMs = Date.parse(msg.timestamp || Date()) || Date.now();
            const _id = deriveId(msg, tsMs)
            const item = { ...msg, _ts: tsMs, _id, importance_score: normalizeScore(msg.importance_score) };
            logger.info('WebSocket', 'Tweet item prepared', { _id, timestamp: msg.timestamp, tsMs });

            setItems((prev) => {
              // drop duplicate by tweet_url or tweet_id
              if (prev.some((x) => x._id && x._id === item._id)) {
                logger.warning('WebSocket', `Duplicate tweet detected, skipping: ${_id}`);
                return prev;
              }
              const next = [...prev];
              let inserted = false;
              for (let i = 0; i < next.length; i++) {
                if (tsMs >= (next[i]._ts || 0)) {
                  next.splice(i, 0, item);
                  inserted = true;
                  break;
                }
              }
              if (!inserted) next.push(item);
              logger.info('WebSocket', 'Tweet added to feed', { _id, position: inserted ? 'sorted' : 'end', totalItems: next.length });
              return next;
            });
            setPage(1);
          } else {
            logger.warning('WebSocket', `Received message with unknown type: ${msg.type}`, msg);
          }
        } catch (e) {
          logger.exception('WebSocket', `Error processing message. Raw data: ${evt.data}`, e);
        }
      });
    }

    // First bootstrap existing items, then connect WS.
    setIsBootstrapping(true)
    bootstrapItems().finally(() => {
      setIsBootstrapping(false)
      connect();
    });

    return () => {
      shouldStop = true;
      reconnectRef.current = 1000;
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      try {
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
          ws.close();
        }
      } catch (e) {
        // ignore
      }
    };
  }, [WS_URL, bootstrapItems]);

  const triggerPullUp = useCallback(async () => {
    if (!apiBaseClean) return

    const now = Date.now()
    // Enforce cooldown: block if within 2 minutes of last pull
    if (lastPulledAt && now - lastPulledAt < 120000) return

    setLastPulledAt(now)
    setIsPulling(true)
    try {
      const url = `${apiBaseClean}/api/pull-up`
      const resp = await fetchWithAuth(url, { method: 'GET', headers: { 'Accept': 'application/json' } })
      const data = await resp.json().catch(() => ({}))
      
      // Check if refresh was skipped due to concurrent automatic polling
      if (data.status === 'skipped') {
        logger.info('pull-up', 'Skipped: automatic polling already in progress')
        // Still bootstrap to get latest items that were just polled
        await bootstrapItems()
      } else {
        // Normal refresh completed
        await bootstrapItems()
      }
    } catch (e) {
      logger.exception('pull-up', 'Pull-up failed', e)
    } finally {
      setIsPulling(false)
    }
  }, [apiBaseClean, lastPulledAt, bootstrapItems])

  // Pass refresh handler to parent
  useEffect(() => {
    if (onRefreshReady && !isBootstrapping) {
      onRefreshReady(() => triggerPullUp)
    }
  }, [onRefreshReady, isBootstrapping, triggerPullUp])

  // Notify parent of pulling state changes
  useEffect(() => {
    if (onPullingChange) {
      onPullingChange(isPulling)
    }
  }, [isPulling, onPullingChange])

  // Derive unique authors list from items.
  const authors = useMemo(() => {
    const map = new Map()
    for (const it of items) {
      const username = (it.author_username ?? '').trim()
      const name = (it.author_name ?? '').trim()
      const key = username || name
      if (!key) continue
      if (!map.has(key)) {
        map.set(key, {
          key,
          label: name && username ? `${name} (@${username})` : (name || (username ? `@${username}` : key)),
        })
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label))
  }, [items])

  // Derive unique query sources from items
  const querySources = useMemo(() => {
    const sources = new Set()
    for (const it of items) {
      const source = it.query_source || it.account || it.account_handle
      if (source) sources.add(source)
    }
    return Array.from(sources).sort((a, b) => a.localeCompare(b))
  }, [items])

  const searchFilteredItems = useMemo(() => {
    const term = searchTerm.trim().toLowerCase()
    if (!term) return items

    return items.filter((it) => {
      const haystacks = [
        it.tweet,
        it.summary,
        it.one_line_summary,
        it.location,
        it.author_name,
        it.author_username,
      ]
      return haystacks.some((field) => (field || '').toString().toLowerCase().includes(term))
    })
  }, [items, searchTerm])

  const filteredItems = useMemo(() => {
    let filtered = searchFilteredItems

    // Apply author filter
    if (selectedAuthor !== 'all') {
      filtered = filtered.filter((it) => {
        const username = (it.author_username ?? '').trim()
        const name = (it.author_name ?? '').trim()
        const key = username || name
        return key === selectedAuthor
      })
      logger.info('Filter', `Author filter "${selectedAuthor}" reduced items from ${searchFilteredItems.length} to ${filtered.length}`)
    }

    // Apply query source filter (specific account or search term)
    if (selectedQuerySource !== 'all') {
      const beforeQueryFilter = filtered.length
      filtered = filtered.filter((it) => {
        const query = it.query_source || it.account || it.account_handle
        return query === selectedQuerySource
      })
      logger.info('Filter', `Query source filter "${selectedQuerySource}" reduced items from ${beforeQueryFilter} to ${filtered.length}`)
    }

    logger.info('Filter', `Final filtered items: ${filtered.length} of ${items.length} total items (page ${page})`)
    return filtered
  }, [searchFilteredItems, selectedAuthor, selectedQuerySource, items.length, page])

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE))
  const pageSafe = Math.min(Math.max(page, 1), totalPages)
  const pageItems = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE
    const pageSlice = filteredItems.slice(start, start + PAGE_SIZE)
    logger.info('Pagination', `Page ${pageSafe}/${totalPages}: Showing items ${start} to ${start + pageSlice.length} (${pageSlice.length} items on this page)`)
    return pageSlice
  }, [filteredItems, pageSafe, totalPages])

  const paginationLocked = isFetchingAllPages

  // Cooldown and relative label for pull-up button
  const cooldownMs = 120000
  const cooldownRemaining = lastPulledAt ? Math.max(0, cooldownMs - (nowTick - lastPulledAt)) : 0

  const pulledLabel = useMemo(() => {
    if (!lastPulledAt) return ''
    const diff = Math.max(0, Math.floor((nowTick - lastPulledAt) / 1000))
    if (diff < 45) return 'Pulled a few seconds ago'
    if (diff < 90) return 'Pulled a minute ago'
    const mins = Math.round(diff / 60)
    return `Pulled ${mins} minutes ago`
  }, [lastPulledAt, nowTick])

  // Reset page when filter or search changes.
  useEffect(() => {
    setPage(1)
  }, [selectedAuthor, searchTerm, selectedQuerySource])

  const isInitialLoading = isBootstrapping && items.length === 0

  useEffect(() => {
    if (onLoadingChange) {
      onLoadingChange(isInitialLoading)
    }
  }, [isInitialLoading, onLoadingChange])

  const scrollToTop = () => {
    if (typeof window !== 'undefined') {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab became hidden - record the time
        lastHiddenTime.current = Date.now()
      } else {
        // Tab became visible - check if we should refresh
        const now = Date.now()
        const FIFTEEN_MINUTES = 15 * 60 * 1000 // 900000ms

        if (lastHiddenTime.current === null) {
          // First time or after page refresh - always refetch
          logger.info('Tab visible', 'First time, refreshing data...')
          bootstrapItems()
        } else {
          const timeDiff = now - lastHiddenTime.current
          if (timeDiff >= FIFTEEN_MINUTES) {
            logger.info('Tab visible', `After ${Math.round(timeDiff / 60000)} minutes, refreshing data...`)
            bootstrapItems()
          } else {
            logger.info('Tab visible', `After ${Math.round(timeDiff / 1000)} seconds (< 15 min), skipping refresh`)
          }
        }
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    }
}, [bootstrapItems])

  return (
    <section id="feed" className={isInitialLoading ? 'feed-loading' : ''}>
      {!isBootstrapping && pulledLabel && (
        <div className="feed-controls feed-controls-top">
          <div className="pull-status">{pulledLabel}</div>
        </div>
      )}

      {items.length === 0 ? (
        <div className="feed-empty">Catch the story before it breaksâ€¦</div>
      ) : (
        <>
          <div className="feed-search">
            <label>
              <span>Search</span>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search tweets, summaries, or authors"
              />
            </label>
          </div>

          <div className="feed-controls">
            <label className="feed-filter">
              <span>Author</span>
              <select value={selectedAuthor} onChange={(e) => setSelectedAuthor(e.target.value)}>
                <option value="all">All Authors</option>
                {authors.map((a) => (
                  <option key={a.key} value={a.key}>{a.label}</option>
                ))}
              </select>
            </label>

            {import.meta.env.VITE_SHOW_QUERY_FILTER === 'true' && (
              <label className="feed-filter">
                <span>Query</span>
                <select value={selectedQuerySource} onChange={(e) => setSelectedQuerySource(e.target.value)}>
                  <option value="all">All Queries</option>
                  {querySources.map((source) => (
                    <option key={source} value={source}>
                      {source.startsWith('@') ? source : `"${source}"`}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>

          {pageItems.map((it) => (
            <TweetCard
              key={it._id || (it.tweet_url || '') + '_' + (it._ts || 0)}
              id={it._id}
              item={it}
              onOpenLightbox={onOpenLightbox}
              isNew={Date.now() - (it._ts || 0) < 2500}
              isRead={readIds.has(it._id)}
              onMarkRead={markRead}
            />
          ))}

          <div className="feed-pagination feed-pagination-bottom">
            <button
              type="button"
              onClick={() => {
                if (paginationLocked) return
                setPage((p) => {
                  const next = Math.max(1, p - 1)
                  if (next !== p) scrollToTop()
                  return next
                })
              }}
                disabled={paginationLocked || pageSafe <= 1}
              >
                {paginationLocked ? <span className="btn-spinner" aria-label="Loading pages"></span> : 'Prev'}
              </button>
            <div className="feed-page-indicator">Page {pageSafe} / {totalPages}</div>
            <button
              type="button"
              onClick={() => {
                if (paginationLocked) return
                setPage((p) => {
                  const next = Math.min(totalPages, p + 1)
                  if (next !== p) scrollToTop()
                  return next
                })
              }}
                disabled={paginationLocked || pageSafe >= totalPages}
              >
                {paginationLocked ? <span className="btn-spinner" aria-label="Loading pages"></span> : 'Next'}
              </button>
          </div>
        </>
      )}
    </section>
  )
}
